import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { editBrandSettingsProductPath } from "@/routes"
import type { ProductRow } from "@/types/products"

import { PriceRange } from "./price-range"
import { ProductGiftTtlForm } from "./product-gift-ttl-form"
import ProductThumbnail from "./product-thumbnail"
import ProductVariants from "./product-variants"

/**
 * OKS-931 — Desktop product list, `hidden md:block`.
 *
 * The `hidden md:block` table + `md:hidden` card grid split is house
 * convention, and `Brand/Rewards/Index.tsx` IS the precedent for it — `:306`
 * and `:398` do exactly this. There is no horizontal scroll at 375px because
 * there is no table at 375px.
 *
 * ⚠️ That citation is valid for the SPLIT and invalid for the SHELL, and an
 * earlier version of this comment got it wrong in both directions.
 *
 * First it cited Rewards as blanket "house convention", which made §3.4's
 * unbuildable column widths look sanctioned — Rewards renders inside
 * `<AppLayout>` (`:191`), the full-width app shell, not `BrandSettingsLayout`,
 * and its widths sum to 724px, so it would have overflowed this page's column
 * too. Then the correction over-swung and said the split claim "doesn't come
 * from Rewards" — which is also false, and would have deleted a true
 * precedent.
 *
 * **The unit is citation + what it is cited FOR.** One file can be a valid
 * precedent for one property and a false one for another, and a
 * filename-level sweep cannot separate them in either direction. Both my
 * original error and my correction of it matched on the filename.
 *
 * Rows are NOT clickable. There is nothing to click through to in this ticket
 * — manual edit is OKS-932 and mission-attach is OKS-933 — so no row-level
 * focus target is introduced (UX §6).
 */
export default function ProductsTable({
  products,
  canManage = false,
  errors,
}: {
  products: ProductRow[]
  /** OKS-935 — gates the TTL editors' affordance. The server is the guard. */
  canManage?: boolean
  /** OKS-935 — render-prop errors from a failed TTL PATCH. */
  errors?: Record<string, string | string[] | undefined>
}) {
  const { t } = useTranslation()

  return (
    <div className="hidden md:block">
      {/*
        ⚠️ `table-fixed` is a STRUCTURAL guarantee, not the thing that fixed
        the bug. Measured, and the distinction is the point.

        Drill (2026-08-03 08:29–08:31 UTC): with `<BrandSettingsLayout wide>`
        in place and `table-fixed` REMOVED, both desktop geometry rows still
        PASS. So `wide` alone is sufficient at the tested viewport, and
        `table-fixed` is not load-bearing there. I predicted the opposite and
        was wrong.

        It is kept anyway, and the reason is narrower than "it fixes the
        overflow": under `table-layout: auto` the table fits only while its
        MIN-CONTENT happens to be under the container width. That held at the
        wide shell and did not at 576px. It is a property of the current copy,
        the current column set and the current viewport — a longer unbroken
        word, a narrower desktop, or a fifth column moves it. `table-fixed`
        makes the table exactly its container's width by construction, so
        `scrollWidth > clientWidth` is unreachable rather than currently-true.

        Do not remove it on the grounds that the rows pass without it. They
        do; that is measured; it is still the weaker guarantee.

        `ui/table.tsx` sets only `w-full`, so the default is `table-layout:
        auto` — which sizes columns to their CONTENT. A 192-character product
        title therefore expanded the product column to fit on one line and the
        table measured **1503px inside a 576px column**. `line-clamp-2` did not
        help: it clamps lines VERTICALLY and, with nothing bounding the cell
        horizontally, the text never had to wrap in the first place.

        Two earlier attempts failed for the same underlying reason and are
        recorded so they are not retried:

          §3.4's fixed widths (64+160+160+160 = 544 in 576) — a real overflow.
          Replacing them with `w-full` + `whitespace-nowrap` — no better:
            `w-full` only redistributes SLACK, and under auto layout there was
            none, because the content decides the width.

        `Table` merges `className` at `ui/table.tsx:13`, so this is a call-site
        change: no `ui/` hand-edit, no exemption-ledger entry, no parity test.

        Verified by measurement, not by reasoning — see the Playwright rows in
        `brand-settings-products.spec.ts`. jsdom does no layout, so Vitest
        cannot see any of this.
      */}
      <Table data-testid="brand-products-table" className="table-fixed">
        <TableHeader>
          <TableRow>
            {/*
              ⚠️ Stale heading removed: there is no `w-full` and no
              `whitespace-nowrap` here any more. Both were the SECOND failed
              attempt, described below.

              UX Spec §3.4 originally fixed image 64 + price 160 + variants
              160 + state 160 = 544px inside a `max-w-xl` (576px) settings
              content column, leaving ~32px for the product title before cell
              padding. The table therefore overflowed and « Retiré de la
              boutique » clipped at rest — `ui/table.tsx` wraps in
              `overflow-x-auto`, so it scrolled rather than breaking the page
              body, which is why it was invisible without measuring.

              ⚠️ That was fixed by `wide` (the column is no longer 576px) plus
              `table-fixed` (the widths below are now CAPS, not advisory
              hints). Under `table-layout: auto` a `w-[…]` on a `<TableHead>`
              is only a PREFERRED width — the browser overrides it to fit
              content — which is why §3.4's numbers were never a budget and
              why replacing them with an idiom was no better. Neither attempt
              failed because the constant was wrong; both failed because there
              was no bound at all.

              Verified by Playwright (`scrollWidth <= clientWidth` on
              `[data-slot="table-container"]`), never by Vitest — jsdom does
              no layout, so `scrollWidth` is always 0 there and the identical
              assertion passes vacuously.
            */}
            {/*
              Under `table-fixed` these are CAPS, not minima — content wraps
              inside them instead of widening the table. The product column is
              deliberately unsized so it absorbs whatever remains.

              ⚠️ No `whitespace-nowrap` here. Under auto layout it made each
              column's min-content a hard floor; under `table-fixed` it would
              make content overflow its own capped cell — the same clipping,
              relocated. These columns are allowed to wrap.
            */}
            <TableHead className="w-[56px]">
              <span className="sr-only">
                {t("settings.brand.products.table.image")}
              </span>
            </TableHead>
            <TableHead>{t("settings.brand.products.table.product")}</TableHead>
            <TableHead className="w-[96px]">
              {t("settings.brand.products.table.price")}
            </TableHead>
            <TableHead className="w-[104px]">
              {t("settings.brand.products.table.variants")}
            </TableHead>
            <TableHead className="w-[112px]">
              {t("settings.brand.products.table.state")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product) => {
            return (
              <TableRow
                /*
                  ⚠️ `product.id`, NEVER `shopify_product_id`. That column is
                  null on every manual row, and `key={null}` is *provided* —
                  React stringifies it to "null" rather than falling back to
                  the index — so two manual products collided on one key:
                  expanding one product's variants could expand another's, and
                  rows mis-reconciled across filter and page changes. It takes
                  exactly two manual rows to reproduce; one is invisible.
                */
                key={product.id}
                data-testid="brand-product-row"
                className={product.archived ? "opacity-60" : undefined}
              >
                <TableCell>
                  <ProductThumbnail
                    src={product.image_url}
                    alt={product.title}
                    /*
                      The `?width=200` merge is a Shopify CDN transform. A
                      manual product's URL is our own Active Storage
                      representation path, where that parameter is noise.
                    */
                    hotlinked={!product.manual}
                  />
                </TableCell>
                <TableCell>
                  {/*
                    `line-clamp-2` + the full value on `title`. Never
                    `truncate` to one line: Shopify titles routinely run past
                    100 characters and one line loses the distinguishing end
                    of a product name (UX §4).

                    ⚠️ The source marker lives HERE, as muted text beneath the
                    title — NOT as a sixth column and NOT as a second badge in
                    the 112px state cell. `ui/badge.tsx` bakes in
                    `whitespace-nowrap w-fit`, so a badge cannot shrink to fit
                    a capped cell (OKS-931 handoff §3/§4, → OKS-946).

                    Only a MANUAL title links: it is the only one with an edit
                    screen to reach. The `manual_hint` TEXT carries the
                    meaning, so the link affordance is never the sole signal
                    of source (UX §6).
                  */}
                  {product.manual ? (
                    <Link
                      href={editBrandSettingsProductPath(product.id)}
                      className="line-clamp-2 hover:underline"
                      title={product.title}
                      data-testid="brand-product-edit-link"
                    >
                      {product.title}
                    </Link>
                  ) : (
                    <span className="line-clamp-2" title={product.title}>
                      {product.title}
                    </span>
                  )}
                  {product.manual ? (
                    <span className="text-muted-foreground block text-xs">
                      {t("settings.brand.products.manual_hint")}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className="tabular-nums">
                  {/* OKS-1018 — shared with the card grid and both mission
                      surfaces; the sr-only connector lives there. */}
                  <PriceRange
                    minCents={product.min_price_cents}
                    maxCents={product.max_price_cents}
                    // ⚠️ Per ROW. A manual product keeps the currency it was
                    // entered in even on a brand whose store declares another.
                    currency={product.currency}
                  />
                </TableCell>
                <TableCell>
                  <ProductVariants
                    product={product}
                    currency={product.currency}
                    canManage={canManage}
                    errors={errors}
                  />
                  {/*
                    OKS-935 S2 — the PRODUCT-level TTL, the tail of the
                    precedence chain and the union for every variant row this
                    table cannot show (other pages; variants filtered out by the
                    archived-view rule). Without it those rows would have no
                    reachable TTL at all (PRD D16 / OQ-7).
                  */}
                  {canManage && (
                    <ProductGiftTtlForm product={product} errors={errors} />
                  )}
                </TableCell>
                <TableCell>
                  {/*
                    Status is never colour-only: an archived row carries the
                    badge TEXT, not just the dimming (UX §6).
                  */}
                  {product.archived ? (
                    /*
                      ⚠️ STILL A REQUIRED OVERRIDE after OKS-946 — but for a
                      DIFFERENT REASON than the one previously recorded here,
                      and the old reason is removed rather than annotated
                      because it is now false.

                      WAS: `ui/badge.tsx` baked in `whitespace-nowrap`, so the
                      badge was always its full single-line width.
                      NOW: OKS-946 removed that. The badge declares no
                      `white-space` at all — so it INHERITS, and `ui/table.tsx`'s
                      `TableCell` declares `whitespace-nowrap`. Without this
                      override the badge inherits NOWRAP from the cell and the
                      behaviour is unchanged.

                      So this line defeats the CELL's declaration, not the
                      badge's. Removing it re-breaks this column; removing it
                      "because OKS-946 fixed the primitive" is the specific
                      mistake to avoid — the primitive fix does not reach
                      inside a `TableCell`.

                      Whether `TableCell` should declare `whitespace-nowrap` at
                      all is OKS-946 OQ-3, deliberately out of scope.

                      ⚠️ The 33.58px figure previously cited here measured the
                      badge ESCAPING its capped cell (past the container's
                      right edge), not clipping. `overflow-hidden` on the badge
                      only clips when something bounds the BADGE; this column
                      bounds the CELL.
                    */
                    <Badge
                      variant="outline"
                      className="whitespace-normal"
                      data-testid="brand-product-archived-badge"
                    >
                      {/*
                        ⚠️ Source-aware TEXT in the SAME badge — not a second
                        badge (D9). « Retiré de la boutique » is simply false
                        for a product the brand archived themselves, and a
                        store-less brand has no boutique to be removed from.
                        Two different facts, one badge, no new column.
                      */}
                      {t(
                        product.manual
                          ? "settings.brand.products.archived_badge_manual"
                          : "settings.brand.products.archived_badge",
                      )}
                    </Badge>
                  ) : null}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
